import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import localFont from "next/font/local";
import { type PropsWithChildren, useEffect } from "react";
import { ThemeProvider } from "~/components/providers/theme-provider";
import type { Locales } from "~/i18n/i18n-types";
import { cn } from "~/utils/cn";
import { useLocale } from "~/utils/use-locale";
import { AIReactPlugin } from "../app-insights/insights";

import "~/components/app-insights/initializers/dependencies";
import "~/components/app-insights/initializers/telemetries";
import { useI18nContext } from "~/i18n/i18n-react";
import { ChangelogRoot } from "../changelog/changelog-root";
import { Toaster } from "../ui/sonner";

const calibri = localFont({
	src: [
		{
			path: "../../../public/fonts/Calibri/Calibri Regular.ttf",
			weight: "400",
			style: "normal",
		},
		{
			path: "../../../public/fonts/Calibri/Calibri Italic.ttf",
			weight: "400",
			style: "italic",
		},
		{
			path: "../../../public/fonts/Calibri/Calibri Light.ttf",
			weight: "300",
			style: "normal",
		},
		{
			path: "../../../public/fonts/Calibri/Calibri Light Italic.ttf",
			weight: "300",
			style: "italic",
		},
		{
			path: "../../../public/fonts/Calibri/Calibri Bold.ttf",
			weight: "700",
			style: "normal",
		},
		{
			path: "../../../public/fonts/Calibri/Calibri Bold Italic.ttf",
			weight: "700",
			style: "italic",
		},
	],
});

const rtlLocales: Array<string | undefined> = ["ar"];

export default function RootLayout({ children }: PropsWithChildren) {
	const locale = useLocale() as Locales;
	const { setLocale } = useI18nContext();

	useEffect(() => {
		if (!(document || window.document)) {
			return;
		}

		const script = document.createElement("script");

		script.innerText =
			"(function(){ var s = document.createElement('script'); var h = document.querySelector('head') || document.body; s.src = 'https://acsbapp.com/apps/app/dist/js/app.js'; s.async = true; s.onload = function(){ acsbJS.init({ statementLink : '', footerHtml : '', hideMobile : false, hideTrigger : false, disableBgProcess : false, language : 'en', position : 'right', leadColor : '#146FF8', triggerColor : '#146FF8', triggerRadius : '50%', triggerPositionX : 'right', triggerPositionY : 'bottom', triggerIcon : 'people', triggerSize : 'bottom', triggerOffsetX : 20, triggerOffsetY : 20, mobile : { triggerSize : 'small', triggerPositionX : 'right', triggerPositionY : 'bottom', triggerOffsetX : 10, triggerOffsetY : 10, triggerRadius : '20' } }); }; h.appendChild(s); })();";

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	useEffect(() => {
		const storedLocale = localStorage.getItem("language") ?? "en";
		if (locale !== storedLocale) {
			setLocale(storedLocale as Locales);
		}
	}, [locale, setLocale]);

	return (
		<AppInsightsContext.Provider value={AIReactPlugin}>
			<ThemeProvider
				defaultTheme="light"
				disableTransitionOnChange
				enableSystem
			>
				<main
					className={cn("contents", calibri.className)}
					dir={rtlLocales.includes(locale) ? "rtl" : "ltr"}
				>
					{children}

					<ChangelogRoot />
					<Toaster position="top-center" />
				</main>
			</ThemeProvider>
		</AppInsightsContext.Provider>
	);
}
